import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { COMMAND_PRIORITY_HIGH, KEY_ENTER_COMMAND } from 'lexical';
import { useEffect } from 'react';

export type RawTextareaEnterPluginProps = {
  onEnter?: () => void;
};

/**
 * Hands a bare Enter to `onEnter` rather than to the editor, which leaves
 * Shift+Enter as the way to add a line. Without `onEnter` it registers nothing
 * and Enter behaves exactly as it always has.
 *
 * `COMMAND_PRIORITY_HIGH` is load-bearing, and so is returning `true`.
 * `registerRichText` registers its own `KEY_ENTER_COMMAND` at
 * `COMMAND_PRIORITY_EDITOR` — the lowest — and Lexical keeps calling handlers
 * down the priorities until one claims the command. Register lower, or return
 * `false` from here, and both run: the caller is told about the Enter *and* a
 * paragraph is inserted, so a form submits with a stray newline in the value it
 * is submitting.
 *
 * Composition is the case worth knowing about. Mid-IME — picking a Japanese or
 * Chinese candidate, say — Enter commits the candidate and must not reach
 * `onEnter`, or the sentence submits half-written the moment the writer accepts
 * a word. `isComposing` on the event is the browser saying so; `editor.isComposing()`
 * is Lexical's own view of the same thing, and they can disagree at the edges of
 * a composition, so both are asked.
 *
 * An empty editor still calls `onEnter`. That is deliberate — "submit an empty
 * form and let it tell me what it wants" is legitimate — so a consumer that
 * wants Enter to do nothing on empty has to say so in its own handler.
 */
export const RawTextareaEnterPlugin = ({
  onEnter,
}: RawTextareaEnterPluginProps) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!onEnter) return;

    return editor.registerCommand(
      KEY_ENTER_COMMAND,
      event => {
        if (event?.shiftKey || event?.isComposing || editor.isComposing()) {
          return false;
        }

        event?.preventDefault();
        onEnter();

        return true;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor, onEnter]);

  return null;
};
